import { Badge } from '@/components/ui/badge';
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { CreditCardIcon, ExternalLinkIcon } from 'lucide-react';
import { format } from 'date-fns';
import type { BillingTransaction, TransactionStatus } from '@repo/types';
import { useCustomerBillingHistory } from '@/lib/hooks/useCustomers';
import { SectionCard, EmptyState, TONE_CLASSES, type Tone } from './_shared';

const STATUS_TONE: Record<TransactionStatus, Tone> = {
  paid: 'success',
  pending: 'warning',
  failed: 'danger',
};

function formatAmount(tx: BillingTransaction): string {
  const amount = Math.round(Number(tx.amount));
  const symbol = tx.currency === 'INR' ? '₹' : `${tx.currency} `;
  return `${symbol}${amount.toLocaleString('en-IN')}`;
}

function formatDate(value?: string | Date | null): string {
  if (!value) return '—';
  return format(new Date(value), 'MMM dd, yyyy');
}

export function CustomerBillingHistory({ customerId }: { customerId: string }) {
  const { data: transactions = [], isLoading } = useCustomerBillingHistory(customerId);

  return (
    <SectionCard icon={CreditCardIcon} title="Billing History" contentClassName="p-0">
      {isLoading ? (
        <div className="space-y-3 p-5">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : transactions.length === 0 ? (
        <EmptyState icon={CreditCardIcon} message="No billing transactions yet" />
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Plan</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="text-right">Invoice</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {transactions.map((tx) => (
                <TableRow key={tx.id}>
                  <TableCell className="font-medium">{tx.plan_name}</TableCell>
                  <TableCell className="tabular-nums">{formatAmount(tx)}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`capitalize ${TONE_CLASSES[STATUS_TONE[tx.status] ?? 'warning']}`}>
                      {tx.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-muted-foreground">
                    {formatDate(tx.transaction_date ?? tx.created_at)}
                  </TableCell>
                  <TableCell className="text-right">
                    {tx.invoice_url ? (
                      <a
                        href={tx.invoice_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
                      >
                        View <ExternalLinkIcon className="h-3.5 w-3.5" aria-hidden />
                      </a>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </SectionCard>
  );
}
